/**
 * Design System - Color Palette
 * Central source of truth for all colors used in the CRO Audit Report
 */

/**
 * Primary category colors
 */
export const PRIMARY_COLORS = {
  'cro-ux': {
    base: 'rgba(59, 130, 246, 0.2)', // Blue
    hover: 'rgba(59, 130, 246, 0.3)',
    text: '#60a5fa'
  },
  'technical': {
    base: 'rgba(20, 184, 166, 0.2)', // Teal
    hover: 'rgba(20, 184, 166, 0.3)',
    text: '#2dd4bf'
  }
} as const;

/**
 * Secondary category colors - organized by primary category
 * Each primary category uses different hues of its base color
 */
export const SECONDARY_COLORS = {
  'cro-ux': {
    visual: {
      base: 'rgba(59, 130, 246, 0.2)', // Blue (lighter)
      hover: 'rgba(59, 130, 246, 0.3)',
      text: '#60a5fa'
    },
    copy: {
      base: 'rgba(37, 99, 235, 0.2)', // Blue (medium)
      hover: 'rgba(37, 99, 235, 0.3)',
      text: '#3b82f6'
    },
    accessibility: {
      base: 'rgba(29, 78, 216, 0.2)', // Blue (darker)
      hover: 'rgba(29, 78, 216, 0.3)',
      text: '#2563eb'
    }
  },
  'technical': {
    performance: {
      base: 'rgba(20, 184, 166, 0.2)', // Teal (lighter)
      hover: 'rgba(20, 184, 166, 0.3)',
      text: '#2dd4bf'
    },
    seo: {
      base: 'rgba(17, 94, 89, 0.2)', // Teal (darker)
      hover: 'rgba(17, 94, 89, 0.3)',
      text: '#14b8a6'
    }
  }
} as const;

/**
 * UI element colors
 */
export const UI_COLORS = {
  background: {
    primary: '#111827',    // gray-900
    secondary: '#1f2937',  // gray-800
    tertiary: '#374151'    // gray-700
  },
  border: {
    primary: '#4b5563',    // gray-600
    secondary: '#6b7280'   // gray-500
  },
  text: {
    primary: '#f9fafb',    // gray-50
    secondary: '#d1d5db',  // gray-300
    tertiary: '#9ca3af'    // gray-400
  },
  scrollbar: {
    track: '#1f2937',      // gray-800
    thumb: '#4b5563',      // gray-600
    thumbHover: '#6b7280'  // gray-500
  }
} as const;

/**
 * Legacy area badge colors (for backward compatibility)
 * These will be gradually phased out in favor of SECONDARY_COLORS
 */
export const LEGACY_AREA_COLORS = {
  visual: {
    base: 'rgba(59, 130, 246, 0.2)',
    hover: 'rgba(59, 130, 246, 0.3)',
    text: '#60a5fa'
  },
  copy: {
    base: 'rgba(168, 85, 247, 0.2)',
    hover: 'rgba(168, 85, 247, 0.3)',
    text: '#c084fc'
  },
  performance: {
    base: 'rgba(20, 184, 166, 0.2)',
    hover: 'rgba(20, 184, 166, 0.3)',
    text: '#2dd4bf'
  },
  seo: {
    base: 'rgba(34, 197, 94, 0.2)',
    hover: 'rgba(34, 197, 94, 0.3)',
    text: '#4ade80'
  },
  accessibility: {
    base: 'rgba(99, 102, 241, 0.2)',
    hover: 'rgba(99, 102, 241, 0.3)',
    text: '#818cf8'
  }
} as const;

/**
 * Semantic colors for specific UI states and components
 */
export const SEMANTIC_COLORS = {
  // Score colors
  score: {
    high: 'stroke-green-400',
    medium: 'stroke-yellow-400',
    low: 'stroke-red-500'
  },

  // Confidence colors
  confidence: {
    high: 'text-green-300',
    medium: 'text-yellow-300',
    low: 'text-red-400'
  },

  // Effort colors
  effort: {
    veryEasy: 'text-green-400',
    easy: 'text-green-400',
    medium: 'text-yellow-400',
    hard: 'text-red-400',
    veryHard: 'text-red-400'
  },

  // Implementation type colors
  implementation: {
    test: {
      background: 'bg-blue-900/50 border-blue-500/50',
      text: 'text-blue-400'
    },
    apply: {
      background: 'bg-green-900/50 border-green-500/50',
      text: 'text-green-400'
    }
  },

  // Device badge colors
  device: {
    both: {
      background: 'bg-purple-900/50 border-purple-500/50',
      text: 'text-purple-400'
    },
    mobile: {
      background: 'bg-blue-900/50 border-blue-500/50',
      text: 'text-blue-400'
    },
    desktop: {
      background: 'bg-cyan-900/50 border-cyan-500/50',
      text: 'text-cyan-400'
    }
  },

  // Top ICE indicator
  topICE: {
    border: 'border-green-400 border-2',
    icon: 'text-green-400',
    lift: 'text-green-400'
  },

  // Page type selection
  pageType: {
    active: 'bg-blue-600 text-white',
    inactive: 'bg-gray-700/60 hover:bg-gray-600 text-gray-300'
  },

  // Alert states
  alerts: {
    warning: {
      background: 'bg-yellow-900/30 border-yellow-700/50',
      text: 'text-yellow-400'
    },
    error: {
      background: 'bg-red-900/30 border-red-700/50',
      text: 'text-red-400'
    }
  },

  // Links and interactive elements
  link: 'text-blue-400 hover:text-blue-300',

  // Mobile-specific section
  mobileSection: {
    background: 'bg-blue-900/20 border-blue-500/30',
    title: 'text-blue-400'
  },

  // CTA buttons
  cta: {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white'
  }
} as const;

/**
 * Card action button colors
 */
export const CARD_ACTION_COLORS = {
  thumbsUp: {
    base: 'text-gray-400',
    active: 'bg-green-900/50 border-green-500 text-green-400',
    hover: 'hover:text-green-400'
  },
  thumbsDown: {
    base: 'text-gray-400',
    active: 'bg-red-900/50 border-red-500 text-red-400',
    hover: 'hover:text-red-400'
  },
  done: {
    base: 'text-gray-400',
    active: 'bg-blue-900/50 border-blue-500 text-blue-400',
    hover: 'hover:text-blue-400'
  },
  notApplicable: {
    base: 'text-gray-400',
    active: 'bg-yellow-900/50 border-yellow-500 text-yellow-400',
    hover: 'hover:text-yellow-400'
  }
} as const;

/**
 * Email report button colors
 */
export const EMAIL_REPORT_COLORS = {
  button: {
    background: 'bg-emerald-600 hover:bg-emerald-700',
    text: 'text-white',
    shadow: 'shadow-lg shadow-emerald-900/30'
  },
  container: {
    background: 'bg-gray-800/90',
    border: 'border-gray-700'
  }
} as const;
